import { logger } from 'adapters/Logger';

import type { OfferPlanAvailability } from '../types';
import { resolveOfferLeadDiversion } from './resolveOfferLeadDiversion';

/** Busca a disponibilidade atual da oferta (bloco `plan` do BFF). */
export type FetchOfferAvailability = (
  planId: string
) => Promise<OfferPlanAvailability | null | undefined>;

/** O /v1/plan/:planId/product tem 60s no axios; o lead não espera tanto. */
export const OFFER_RECHECK_TIMEOUT_MS = 5000;

type Diversion = ReturnType<typeof resolveOfferLeadDiversion>;

const withTimeout = <T>(promise: Promise<T>, timeoutMs: number) =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('Offer availability recheck timeout')),
      timeoutMs
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

/**
 * FIN-8011: a disponibilidade é lida uma vez no SSR. Se a página ficou aberta
 * e a oferta abriu (NOT_STARTED -> AVAILABLE) ou teve o prazo adiado
 * (EXPIRED -> AVAILABLE), ou a vaga voltou (SOLD_OUT -> AVAILABLE, FIN-8119:
 * PIX/boleto pendente expirou), o valor guardado está velho. Por isso, só quando o
 * valor guardado desviaria, consulta o BFF de novo e decide com o valor atual.
 * Oferta guardada como disponível não faz requisição extra. Erro ou timeout
 * na consulta: decide com o valor guardado (comportamento anterior).
 */
export async function resolveOfferLeadDiversionFresh({
  isOfferFlow,
  offerAvailability,
  leadRoute,
  planId,
  business,
  fetchAvailability,
  timeoutMs = OFFER_RECHECK_TIMEOUT_MS,
}: {
  isOfferFlow: boolean;
  offerAvailability: OfferPlanAvailability | null | undefined;
  leadRoute?: string | null;
  planId: string | null | undefined;
  business: string | null | undefined;
  fetchAvailability?: FetchOfferAvailability;
  timeoutMs?: number;
}): Promise<{
  diversion: Diversion;
  /** Disponibilidade usada na decisão (a atual, quando a consulta deu certo). */
  offerAvailability: OfferPlanAvailability | null | undefined;
  /** true quando a consulta ao BFF trouxe um valor novo. */
  refreshed: boolean;
}> {
  const storedDiversion = resolveOfferLeadDiversion({
    isOfferFlow,
    offerAvailability,
    leadRoute,
  });

  if (!storedDiversion.divert || !fetchAvailability || !planId) {
    return { diversion: storedDiversion, offerAvailability, refreshed: false };
  }

  const storedStatus = offerAvailability?.availability ?? null;

  try {
    const fresh = await withTimeout(fetchAvailability(planId), timeoutMs);

    // BFF sem o bloco `plan`: sem informação nova, fica o valor guardado.
    if (!fresh?.availability) {
      throw new Error('Offer availability recheck without plan');
    }

    const diversion = resolveOfferLeadDiversion({
      isOfferFlow,
      offerAvailability: fresh,
      leadRoute,
    });

    logger.info(
      new Error('Offer availability rechecked'),
      { flow: 'offer-availability', operation: 'lead-recheck' },
      {
        planId,
        business: business ?? null,
        storedAvailability: storedStatus,
        freshAvailability: fresh.availability,
        divert: diversion.divert,
      }
    );

    return { diversion, offerAvailability: fresh, refreshed: true };
  } catch (error) {
    logger.warning(
      new Error('Offer availability recheck failed'),
      { flow: 'offer-availability', operation: 'lead-recheck' },
      {
        planId,
        business: business ?? null,
        storedAvailability: storedStatus,
        error: error instanceof Error ? error.message : String(error),
      }
    );

    return { diversion: storedDiversion, offerAvailability, refreshed: false };
  }
}
